/**
 * The resources with no agreed capacity basis — inspection, finishing, process.
 *
 * They are excluded from every ratio on this surface and appear here as hours only. That is
 * not a claim that they are unconstrained; it is an admission that nobody has told us what
 * constrains them. Measuring a bench against a 24-hour machine calendar is what once made
 * this grid a sea of red and a 3D printer read 1,342% (spec §10d).
 *
 * Collapsed by default because they are context, not the question — but present, because a
 * page that silently dropped 47% of the scheduled hours would be lying by omission.
 */

import React from 'react'
import type { ProcessGroup } from '../../viewmodels/loading'
import { hrs } from './format'

function List({ groups }: { groups: ProcessGroup[] }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-x-3.5 gap-y-1.5 px-[13px] pb-[13px] lg:grid-cols-4">
      {groups.map((g) => (
        <div
          key={`${g.site}:${g.name}`}
          className="flex items-center justify-between gap-2 border-b py-1"
          style={{ borderColor: 'var(--lc-b05)' }}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap font-sans text-[11px]" style={{ color: 'var(--lc-ink3)' }}>
            {g.name}
          </span>
          <span className="font-mono text-[11px] font-medium tabular-nums" style={{ color: 'var(--lc-ink)' }}>
            {hrs(g.hours)}
          </span>
        </div>
      ))}
    </div>
  )
}

export default function ProcessStrip({ groups, hours }: {
  groups: ProcessGroup[]
  hours: number
}): React.JSX.Element | null {
  const [open, setOpen] = React.useState(false)
  if (!groups.length) return null
  return (
    <div
      className="mx-[22px] mt-3.5 overflow-hidden rounded-[9px] border border-dashed"
      style={{ borderColor: 'var(--lc-b14)', background: 'var(--lc-panel)' }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-[13px] py-[9px] text-left"
      >
        <span className="font-sans text-[11px]" style={{ color: 'var(--lc-mut)' }}>
          {groups.length} process &amp; inspection groups · {hrs(hours)} · scheduled hours only, no agreed
          capacity basis
        </span>
        <span
          className="whitespace-nowrap font-mono text-[10px] font-semibold tracking-[.08em]"
          style={{ color: 'var(--lc-blue)' }}
        >
          {open ? 'HIDE ▴' : 'SHOW ▾'}
        </span>
      </button>
      {open && <List groups={groups} />}
    </div>
  )
}
